/**
 * GLAMRDiP gift-with-purchase reconciler — Joel 2026-05-02.
 *
 * Reads window.__gdGiftRules (emitted by sections/gift-with-purchase-rules.liquid)
 * and ensures reward products are in cart iff at least one trigger product is.
 *
 * Bidirectional reconciliation:
 *   - Trigger added → reward auto-added with property _free_gift: 'true'
 *   - Trigger removed (last one) → reward auto-removed
 *   - User direct-POSTs to /cart/add.js for a reward product without trigger
 *     → next reconcile pass removes it (anti-abuse, Option B from the spec).
 *
 * Triggers fire on:
 *   - page load
 *   - 'cart:updated' DOM event (theme-wide cart-change broadcast)
 *   - any /cart/add.js, /cart/change.js, /cart/update.js fetch completion
 *     (caught via window.fetch monkey-patch)
 *
 * Anti-recursion via _processing flag — fetch monkey-patch fires
 * reconcileCart() which itself calls fetch('/cart/add.js'), which would
 * re-enter; the flag short-circuits.
 *
 * Failure modes (silent — surface via console.warn):
 *   - /cart.js fetch fails → no reconcile this tick; next event retries
 *   - /cart/add.js for reward fails (sold out, OOS, tracking enabled +
 *     stock=0) → log + skip; cart drawer renders without the gift
 *   - rule has no triggers OR no rewards → rule skipped (Liquid filter
 *     already drops these but defensive)
 */
(function () {
  'use strict';

  const rules = (window.__gdGiftRules || []).filter(
    (r) => r && r.enabled && Array.isArray(r.triggers) && r.triggers.length > 0 && Array.isArray(r.rewards) && r.rewards.length > 0
  );
  if (!rules.length) return;

  // Joel 2026-05-02: shared coordination flag with gd-cart-sanitizer.js.
  // Both modules check window.__gdCartProcessing before running. Whichever
  // fires first sets the flag; the other skips that cycle. Initialize
  // here in case the sanitizer hasn't loaded yet.
  if (typeof window.__gdCartProcessing !== 'boolean') {
    window.__gdCartProcessing = false;
  }
  let pendingTimer = 0;

  function debouncedReconcile(delay) {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pendingTimer = 0;
      reconcileCart();
    }, delay || 150);
  }

  async function reconcileCart() {
    if (window.__gdCartProcessing) return;
    window.__gdCartProcessing = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null));
      if (!cart || !Array.isArray(cart.items)) return;

      // Joel 2026-05-02: track which rule a reward came from so we can
      // stamp the matched rule's caption onto the line item. When
      // multiple rules share a reward (rare), the first-matching rule
      // wins — deterministic by rule order in the customizer block list.
      const wantedRewards = new Map(); // variant_id → caption
      const DEFAULT_CAPTION = 'Gift with purchase — included free';
      for (const rule of rules) {
        // Trigger present? Match by product_id (any variant of the trigger
        // product counts) — kits often have multiple variants but same
        // product_id, so this matches starter / complete / ultimate kits
        // when any variant is in cart.
        const triggerInCart = rule.triggers.some((triggerProductId) =>
          cart.items.some((item) => Number(item.product_id) === Number(triggerProductId))
        );
        if (triggerInCart) {
          const caption = (rule.caption && String(rule.caption).trim()) || DEFAULT_CAPTION;
          rule.rewards.forEach((rewardVariantId) => {
            const vid = Number(rewardVariantId);
            // First-matching-rule wins for shared rewards.
            if (!wantedRewards.has(vid)) wantedRewards.set(vid, caption);
          });
        }
      }

      // Compute add list (wanted but not in cart)
      const itemsByVariant = new Map();
      cart.items.forEach((it) => itemsByVariant.set(Number(it.variant_id), it));
      const toAdd = [];
      wantedRewards.forEach((caption, vid) => {
        if (!itemsByVariant.has(vid)) {
          toAdd.push({
            id: vid,
            quantity: 1,
            properties: {
              _free_gift: 'true',
              _no_edit: 'true',
              _gift_caption: caption,
            },
          });
        }
      });

      // Compute remove list (currently in cart with _free_gift property but no longer wanted)
      const toRemove = [];
      cart.items.forEach((it) => {
        const isFreeGift = it.properties && it.properties._free_gift === 'true';
        if (isFreeGift && !wantedRewards.has(Number(it.variant_id))) {
          toRemove.push(it.key);
        }
      });

      // Apply removes first (avoid race where a stale gift + new gift both exist)
      if (toRemove.length) {
        for (const key of toRemove) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: key, quantity: 0 }),
            });
          } catch (e) {
            console.warn('[gd-gift] remove failed:', key, e.message);
          }
        }
      }

      // Apply adds
      if (toAdd.length) {
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: toAdd }),
          });
        } catch (e) {
          console.warn('[gd-gift] add failed:', e.message);
        }
      }

      if (toAdd.length || toRemove.length) {
        // Notify cart drawer + any other surface that depends on cart state.
        // Use both event names to cover all listeners (some listen for
        // cart:updated, others for product-added-to-cart).
        document.body.dispatchEvent(new CustomEvent('cart:updated'));
        if (toAdd.length) {
          document.body.dispatchEvent(new CustomEvent('product-added-to-cart'));
        }
      }
    } catch (e) {
      console.warn('[gd-gift] reconcile error:', e);
    } finally {
      window.__gdCartProcessing = false;
    }
  }

  // Initial reconcile on page load — handles refresh-with-cart cases.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => debouncedReconcile(300));
  } else {
    debouncedReconcile(300);
  }

  // Cart-change events
  document.body.addEventListener('cart:updated', () => debouncedReconcile(120));
  document.body.addEventListener('product-added-to-cart', () => debouncedReconcile(120));
  document.body.addEventListener('gd:cart-refreshed', () => debouncedReconcile(120));

  // fetch monkey-patch: catches direct /cart/add, /cart/change, /cart/update
  // POSTs from any code path (Dawn forms, our gd-cart-add.js, kit-builder,
  // instant-gels-builder, even DevTools-issued curl-like requests). When
  // any of those fires, schedule a reconcile. The processing flag prevents
  // recursion when reconcileCart()'s own fetches re-trigger this hook.
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = originalFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)\.js/.test(url)) {
      result.then(() => debouncedReconcile(150)).catch(() => {});
    }
    return result;
  };
})();
